import { useEffect, useState } from "react";
import { Toaster } from "react-hot-toast";
import { useLocation, useNavigate } from "react-router-dom";
import CustomRoutes from "./components/CustomRoutes/CustomRoutes";
import LoadingFallback from "./components/UI/LoadingFallback/LoadingFallback";
import { AuthServiceInstance } from "./features/auth/AuthService";
import { loginUser } from "./features/auth/redux/authSlice";
import { restoreReportAuthToken } from "./features/magic-mirror/api/reportAuthApi";
import { checkValidToken, setUrlRejectAfterLogin } from "./helpers/auth";
import { useAppDispatch } from "./store/store";
import { ConfigProvider } from "antd";
import viVN from "antd/es/locale/vi_VN";
import usePageTitle from "./hooks/usePageTitle";
import { useSelector } from "react-redux";
import GlobalModal from "./components/GlobalModal";
import {
  onCloseModal,
  resetListModal,
} from "./components/GlobalModal/globalModalSlice";
import { getWorkspaceInfo } from "./features/system-management/workspace/redux/workspaceSlice";
import { ROUTE_PATH_NAME_BY_ID } from "./helpers/permissionConstant";
import { useTranslation } from "react-i18next";
import {
  canShowChatBot,
  persistSsoClientIds,
} from "./components/EmbeddedIframe/EmbeddedIframe";
import EmbeddedIframe from "./components/EmbeddedIframe/EmbeddedIframe";
import { TicketExportResumer } from "./features/g-process/report/components/TicketExportResumer";

function App() {
  const location = useLocation();
  const dispatch = useAppDispatch();
  const { t: tm } = useTranslation("message", { keyPrefix: "login" });
  const [checkingUser, setCheckingUser] = useState<boolean>(true);
  const { ListModals, isOpen } = useSelector((state: any) => state.globalModal);
  const shouldShowChatBot = canShowChatBot();

  const navigate = useNavigate();

  // useEffect(() => {
  //   if (isMobile && location.pathname !== "/mobile-detected")
  //     redirect("/mobile-detected");
  // });

  useEffect(() => {
    // report-GT: seed lại JWT report-GT đã lưu để request /api/reports/* sau reload
    // có token ngay, trước khi loginUser phát lại token (§1).
    restoreReportAuthToken();

    // when SSO redirect to login/callback, don't check user authentication
    if (location.pathname === "/login/callback") {
      setCheckingUser(false);
      return;
    }

    // check user authentication
    AuthServiceInstance.getUser().then((user) => {
      if (user) {
        persistSsoClientIds(
          typeof user.profile?.client_ids === "string"
            ? user.profile.client_ids
            : null
        );
      }

      if (user && checkValidToken(user.access_token, user.expires_at)) {
        const callback = (_: any, userInfo: any) => {
          if (!userInfo) {
            navigate(ROUTE_PATH_NAME_BY_ID.UNAUTHENTICATED);
          } else if (userInfo?.status === 0) {
            navigate(ROUTE_PATH_NAME_BY_ID.ACCOUNT_NOT_WORKSPACE, {
              state: {
                message: `${tm("accountInactive")}. ${tm(
                  "pleaseContactManagerOr"
                )}`,
                hotline1: tm("hotline222"),
                hotline2: tm("hotline9999"),
              },
            });
          } else if (!userInfo?.workspace || userInfo?.workspace.status === 0) {
            // case workspace bị inactive và null
            setCheckingUser(false);
            return navigate(ROUTE_PATH_NAME_BY_ID.ACCOUNT_NOT_WORKSPACE);
          }
          if (checkingUser) setCheckingUser(false);
          if (userInfo?.is_admin) {
            // Get workspace info admin
            getWorkspaceInfo(dispatch);
          }
        };
        loginUser(dispatch, user, callback);
      } else {
        setUrlRejectAfterLogin(location?.pathname + location?.search);
        AuthServiceInstance.login();
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps

  }, []);

  usePageTitle({ pathname: location.pathname });

  if (checkingUser) return <LoadingFallback />;

  return (
    <>
      {shouldShowChatBot && (
        <EmbeddedIframe
          title="global-embedded-app"
          src={`${process.env.REACT_APP_CHAT_BOT_URL}/`}
          parentOrigin={process.env.REACT_APP_BASE_URL}
          allowedOrigin={process.env.REACT_APP_CHAT_BOT_URL}
        />
      )}

      <ConfigProvider locale={viVN}>
        <Toaster
          position="bottom-right"
          toastOptions={{
            duration: 3000,
            style: {
              background: "#fff",
              color: "#000 ",
              padding: "1.25rem 1.5rem",
            },
            success: {
              style: {
                border: "1px solid #95DBD2",
              },
            },
            error: {
              style: {
                border: "1px solid #DB9595",
              },
            },
          }}
        />
        <TicketExportResumer />
        <CustomRoutes />
        <GlobalModal
          isOpen={isOpen}
          onClose={() => {
            dispatch(onCloseModal());
            dispatch(resetListModal());
          }}
        >
          {ListModals[ListModals.length - 1]}
        </GlobalModal>
      </ConfigProvider>
    </>
  );
}

export default App;
